import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ChevronLeft, Loader2, UserPlus, Users, Mail, Shield, 
  Trash2, Edit, Search, Phone, Building, MapPin, Tag,
  DollarSign, Percent, Copy, Plus, Calendar, Download, FileText,
  CheckCircle, UserCheck, AlertCircle
} from 'lucide-react';

// Sortable table header component
const SortableHeader = ({ field, sortField, sortDirection, onSort, children, className = "" }) => (
  <th 
    className={`px-4 py-4 text-left text-xs font-medium text-white/50 uppercase cursor-pointer hover:text-white/80 transition-colors ${className}`}
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      {children}
      {sortField === field && (
        <span className="text-[#CCFF00]">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </div>
  </th>
);

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('users');
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'customer'
  });
  
  const [editProfile, setEditProfile] = useState({
    name: '',
    phone: '',
    company_name: '',
    abn: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_postcode: '',
    shipping_same_as_billing: true,
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postcode: '',
    notes: ''
  });

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    description: '',
    customer_email: '',
    min_order_value: null,
    expires_at: ''
  });

  const fetchData = async () => {
    try {
      const [usersRes, couponsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        axios.get(`${API}/coupons`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }).catch(() => ({ data: { coupons: [] } }))
      ]);
      setUsers(usersRes.data.users || []);
      setCoupons(couponsRes.data.coupons || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // User Management Functions
  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('User created successfully');
      setShowAddDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'customer' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
    setSaving(false);
  };

  const handleEditUser = async (user) => {
    setSelectedUser(user);
    // Fetch user profile
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setEditProfile({
        name: user.name || '',
        phone: res.data.phone || user.phone || '',
        company_name: res.data.company_name || '',
        abn: res.data.abn || '',
        billing_address: res.data.billing_address || '',
        billing_city: res.data.billing_city || '',
        billing_state: res.data.billing_state || '',
        billing_postcode: res.data.billing_postcode || '',
        shipping_same_as_billing: res.data.shipping_same_as_billing !== false,
        shipping_address: res.data.shipping_address || '',
        shipping_city: res.data.shipping_city || '',
        shipping_state: res.data.shipping_state || '',
        shipping_postcode: res.data.shipping_postcode || '',
        notes: res.data.notes || ''
      });
    } catch {
      setEditProfile({
        name: user.name || '',
        phone: user.phone || '',
        company_name: '',
        abn: '',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_postcode: '',
        shipping_same_as_billing: true,
        shipping_address: '',
        shipping_city: '',
        shipping_state: '',
        shipping_postcode: '',
        notes: ''
      });
    }
    setShowEditDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/profile`, editProfile, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Profile updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setSaving(false);
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, 
        { active: !currentActive },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success(currentActive ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleToggleInvoice = async (userId, currentInvoiceEnabled) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/invoice-permission`, 
        { invoice_enabled: !currentInvoiceEnabled },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success(!currentInvoiceEnabled ? 'Invoice payment enabled' : 'Invoice payment disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update invoice permission');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('User deleted successfully');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
    setSaving(false);
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  // Coupon Functions
  const handleSaveCoupon = async () => {
    if (!newCoupon.code || !newCoupon.discount_value) {
      toast.error('Please fill in code and discount value');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/coupons`, newCoupon, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Coupon created successfully');
      setShowCouponDialog(false);
      setNewCoupon({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        description: '',
        customer_email: '',
        min_order_value: null,
        expires_at: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
    setSaving(false);
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      await axios.delete(`${API}/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Coupon deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied!');
  };

  const exportUsers = () => {
    // Create CSV data
    const headers = ['Name', 'Email', 'Phone', 'Role', 'Invoice Enabled', 'Active', 'Created At', 'Company', 'ABN'];
    const rows = filteredUsers.map(user => [
      user.name || '',
      user.email || '',
      user.phone || '',
      user.role || '',
      user.invoice_enabled ? 'Yes' : 'No',
      user.active !== false ? 'Yes' : 'No',
      user.created_at ? new Date(user.created_at).toLocaleDateString('en-AU') : '',
      user.company_name || '',
      user.abn || ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Users exported successfully');
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter || 
                       (roleFilter === 'guest' && user.is_guest);
    const matchesInvoice = invoiceFilter === 'all' || 
                          (invoiceFilter === 'enabled' && user.invoice_enabled) ||
                          (invoiceFilter === 'disabled' && !user.invoice_enabled);
    return matchesSearch && matchesRole && matchesInvoice;
  });

  // Sort users
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortField) {
      case 'name':
        aVal = a.name?.toLowerCase() || '';
        bVal = b.name?.toLowerCase() || '';
        break;
      case 'email':
        aVal = a.email?.toLowerCase() || '';
        bVal = b.email?.toLowerCase() || '';
        break;
      case 'role':
        aVal = a.is_guest ? 'guest' : (a.role || '');
        bVal = b.is_guest ? 'guest' : (b.role || '');
        break;
      case 'email_verified':
        aVal = a.email_verified ? 1 : 0;
        bVal = b.email_verified ? 1 : 0;
        break;
      case 'invoice_enabled':
        aVal = a.invoice_enabled ? 1 : 0;
        bVal = b.invoice_enabled ? 1 : 0;
        break;
      case 'created_at':
      default:
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
        break;
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  // Handlers for new guest/verify actions
  const handleCreateFromGuest = async (email) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/users/create-from-guest`, 
        { email },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success(`Account created! Temp password: ${res.data.temp_password}`, { duration: 10000 });
      // Copy password to clipboard
      navigator.clipboard.writeText(res.data.temp_password);
      toast.info('Password copied to clipboard');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
    setSaving(false);
  };

  const handleVerifyEmail = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/verify-email`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Email verified successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify email');
    }
  };

  const handleResendVerification = async (userId, email) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success(`Verification email sent to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="outline" size="icon" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              </Link>
              <div>
                <span className="overline text-[#CCFF00]">Management</span>
                <h1 className="text-2xl font-['Chivo'] font-black text-white">Customers & Users</h1>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger value="users" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Users className="w-4 h-4 mr-2" />
                Users ({users.length})
              </TabsTrigger>
              <TabsTrigger value="coupons" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Tag className="w-4 h-4 mr-2" />
                Coupons ({coupons.length})
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-6">
              {/* Filters & Actions */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-white/5 border-white/10 text-white pl-10"
                    />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-[150px] bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Filter role" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      <SelectItem value="all" className="text-white">All Roles</SelectItem>
                      <SelectItem value="admin" className="text-white">Admin</SelectItem>
                      <SelectItem value="customer" className="text-white">Customer</SelectItem>
                      <SelectItem value="guest" className="text-white">Guest (No Account)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
                    <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Invoice Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      <SelectItem value="all" className="text-white">All Invoice Status</SelectItem>
                      <SelectItem value="enabled" className="text-white">Invoice Enabled</SelectItem>
                      <SelectItem value="disabled" className="text-white">Invoice Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={exportUsers}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 rounded-none"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                  <Button
                    onClick={() => setShowAddDialog(true)}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </div>

              {/* Users List */}
              <div className="bg-[#111] border border-white/10 rounded-sm">
                {loading ? (
                  <div className="p-12 text-center">
                    <Loader2 className="w-8 h-8 text-[#CCFF00] mx-auto animate-spin" />
                  </div>
                ) : sortedUsers.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No users found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white/5 border-b border-white/10">
                        <tr>
                          <SortableHeader field="name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>User</SortableHeader>
                          <SortableHeader field="role" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>Role</SortableHeader>
                          <SortableHeader field="email_verified" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-center">Verified</SortableHeader>
                          <th className="px-4 py-4 text-center text-xs font-medium text-white/50 uppercase">Status</th>
                          <SortableHeader field="invoice_enabled" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-center">Invoice</SortableHeader>
                          <SortableHeader field="created_at" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>Joined</SortableHeader>
                          <th className="px-4 py-4 text-left text-xs font-medium text-white/50 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                        {sortedUsers.map((user) => (
                          <tr key={user.user_id || user.email} className="hover:bg-white/5">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.is_guest ? 'bg-orange-500/10' : 'bg-[#CCFF00]/10'}`}>
                                  <span className={user.is_guest ? 'text-orange-400 font-bold' : 'text-[#CCFF00] font-bold'}>
                                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                                  </span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-white font-medium">{user.name || 'Unknown'}</p>
                                    {user.is_guest && (
                                      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-xs">
                                        Guest
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-white/50 text-sm">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {user.is_guest ? (
                                <Badge variant="outline" className="text-orange-400 border-orange-400/30">Guest</Badge>
                              ) : (
                                <Select 
                                  value={user.role} 
                                  onValueChange={(v) => handleChangeRole(user.user_id, v)}
                                  disabled={user.email === 'admin@3dprintbureau.com.au'}
                                >
                                  <SelectTrigger className="w-[110px] bg-white/5 border-white/10 text-white h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#111] border-white/10">
                                    <SelectItem value="admin" className="text-white">Admin</SelectItem>
                                    <SelectItem value="customer" className="text-white">Customer</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="px-4 py-4 text-center">
                              {user.is_guest ? (
                                <span className="text-orange-400 text-xs">N/A</span>
                              ) : user.email_verified ? (
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                  <span className="text-green-400 text-xs">Verified</span>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center gap-1">
                                  <div className="flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4 text-yellow-400" />
                                    <span className="text-yellow-400 text-xs">Pending</span>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs h-6 px-2 text-[#CCFF00] hover:text-[#CCFF00] hover:bg-[#CCFF00]/10"
                                      onClick={() => handleVerifyEmail(user.user_id)}
                                    >
                                      Verify
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs h-6 px-2 text-blue-400 hover:text-blue-400 hover:bg-blue-400/10"
                                      onClick={() => handleResendVerification(user.user_id, user.email)}
                                      disabled={saving}
                                    >
                                      <Mail className="w-3 h-3 mr-1" />
                                      Resend
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-4 text-center">
                              {user.is_guest ? (
                                <span className="text-white/30 text-xs">-</span>
                              ) : (
                                <div className="flex flex-col items-center gap-1">
                                  <Switch
                                    checked={user.active !== false}
                                    onCheckedChange={() => handleToggleActive(user.user_id, user.active !== false)}
                                    disabled={user.email === 'admin@3dprintbureau.com.au'}
                                    className="data-[state=checked]:bg-green-500"
                                  />
                                  <span className={`text-xs ${user.active !== false ? 'text-green-400' : 'text-red-400'}`}>
                                    {user.active !== false ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-4 text-center">
                              {user.role !== 'admin' && !user.is_guest && (
                                <div className="flex flex-col items-center gap-1">
                                  <Switch
                                    checked={user.invoice_enabled === true}
                                    onCheckedChange={() => handleToggleInvoice(user.user_id, user.invoice_enabled === true)}
                                    className="data-[state=checked]:bg-[#CCFF00]"
                                  />
                                  <span className={`text-xs ${user.invoice_enabled ? 'text-[#CCFF00]' : 'text-white/30'}`}>
                                    {user.invoice_enabled ? 'Enabled' : 'Card Only'}
                                  </span>
                                </div>
                              )}
                              {user.is_guest && <span className="text-white/30 text-xs">-</span>}
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-white/50 text-sm">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString('en-AU') : 'N/A'}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-1">
                                {user.is_guest ? (
                                  <Button
                                    size="sm"
                                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] h-8 px-3 text-xs"
                                    onClick={() => handleCreateFromGuest(user.email)}
                                    disabled={saving}
                                  >
                                    <UserCheck className="w-3 h-3 mr-1" />
                                    Create Account
                                  </Button>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-[#CCFF00] hover:text-[#CCFF00] hover:bg-[#CCFF00]/10 h-8 w-8 p-0"
                                      onClick={() => handleEditUser(user)}
                                      title="Edit Profile"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-400 hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0"
                                      onClick={() => { setSelectedUser(user); setShowDeleteDialog(true); }}
                                      disabled={user.email === 'admin@3dprintbureau.com.au'}
                                      title="Delete User"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Coupons Tab */}
            <TabsContent value="coupons" className="space-y-6">
              <div className="flex justify-end">
                <Button
                  onClick={() => setShowCouponDialog(true)}
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Coupon
                </Button>
              </div>

              <div className="bg-[#111] border border-white/10 rounded-sm">
                {coupons.length === 0 ? (
                  <div className="p-12 text-center">
                    <Tag className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No coupons yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/10">
                    {coupons.map((coupon) => (
                      <div key={coupon.coupon_id} className="p-4 hover:bg-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="bg-[#CCFF00]/10 px-3 py-2 rounded">
                              <span className="text-[#CCFF00] font-mono font-bold">{coupon.code}</span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                                  {coupon.discount_type === 'percentage' 
                                    ? `${coupon.discount_value}% OFF` 
                                    : `$${coupon.discount_value} OFF`}
                                </Badge>
                                {coupon.customer_email && (
                                  <Badge variant="outline" className="text-white/50 border-white/20">
                                    {coupon.customer_email}
                                  </Badge>
                                )}
                              </div>
                              {coupon.description && (
                                <p className="text-white/50 text-sm mt-1">{coupon.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-white/50 hover:text-white"
                              onClick={() => copyCode(coupon.code)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-400 hover:bg-red-400/10"
                              onClick={() => handleDeleteCoupon(coupon.coupon_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#CCFF00]" />
              Add New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/70">Full Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label className="text-white/70">Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label className="text-white/70">Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label className="text-white/70">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10">
                  <SelectItem value="customer" className="text-white">Customer</SelectItem>
                  <SelectItem value="admin" className="text-white">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleAddUser} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#CCFF00]" />
              Edit Customer Profile - {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Personal Info */}
            <div>
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#CCFF00]" />
                Personal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Name</Label>
                  <Input
                    value={editProfile.name}
                    onChange={(e) => setEditProfile(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Phone</Label>
                  <Input
                    value={editProfile.phone}
                    onChange={(e) => setEditProfile(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Business Info */}
            <div>
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Building className="w-4 h-4 text-[#CCFF00]" />
                Business Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Company Name</Label>
                  <Input
                    value={editProfile.company_name}
                    onChange={(e) => setEditProfile(prev => ({ ...prev, company_name: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">ABN</Label>
                  <Input
                    value={editProfile.abn}
                    onChange={(e) => setEditProfile(prev => ({ ...prev, abn: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Billing Address */}
            <div>
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#CCFF00]" />
                Billing Address
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-white/70">Street Address</Label>
                  <Input
                    value={editProfile.billing_address}
                    onChange={(e) => setEditProfile(prev => ({ ...prev, billing_address: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-white/70">City</Label>
                    <Input
                      value={editProfile.billing_city}
                      onChange={(e) => setEditProfile(prev => ({ ...prev, billing_city: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">State</Label>
                    <Input
                      value={editProfile.billing_state}
                      onChange={(e) => setEditProfile(prev => ({ ...prev, billing_state: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Postcode</Label>
                    <Input
                      value={editProfile.billing_postcode}
                      onChange={(e) => setEditProfile(prev => ({ ...prev, billing_postcode: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-white/70">Admin Notes</Label>
              <Textarea
                value={editProfile.notes}
                onChange={(e) => setEditProfile(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="Internal notes about this customer..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-white/70">
              Are you sure you want to delete <span className="text-white font-bold">{selectedUser?.name}</span>?
            </p>
            <p className="text-white/50 text-sm mt-2">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} disabled={saving} className="bg-red-500 text-white hover:bg-red-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#CCFF00]" />
              Create Coupon
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/70">Coupon Code *</Label>
              <Input
                value={newCoupon.code}
                onChange={(e) => setNewCoupon(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="bg-white/5 border-white/10 text-white mt-2 font-mono"
                placeholder="SAVE10"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Discount Type</Label>
                <Select 
                  value={newCoupon.discount_type} 
                  onValueChange={(v) => setNewCoupon(prev => ({ ...prev, discount_type: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    <SelectItem value="percentage" className="text-white">Percentage (%)</SelectItem>
                    <SelectItem value="fixed" className="text-white">Fixed ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70">Value *</Label>
                <Input
                  type="number"
                  value={newCoupon.discount_value}
                  onChange={(e) => setNewCoupon(prev => ({ ...prev, discount_value: parseFloat(e.target.value) }))}
                  className="bg-white/5 border-white/10 text-white mt-2"
                />
              </div>
            </div>
            <div>
              <Label className="text-white/70">Description</Label>
              <Input
                value={newCoupon.description}
                onChange={(e) => setNewCoupon(prev => ({ ...prev, description: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="10% off first order"
              />
            </div>
            <div>
              <Label className="text-white/70">Restrict to Email (optional)</Label>
              <Input
                value={newCoupon.customer_email}
                onChange={(e) => setNewCoupon(prev => ({ ...prev, customer_email: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="customer@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCouponDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleSaveCoupon} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Coupon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminUsers;
